import { useCallback, useMemo, useState } from "react";
import { useMutation, useQuery } from "@apollo/client/react";
import { useIntl } from "react-intl";
import { graphQlErrorExtensions } from "@/lib/graphql-error";
import { TOO_MANY_MATCH_COMPANIES } from "@/lib/genesis-error-codes";
import { userErrorMessage } from "@/lib/user-error";
import {
  AddCompanyToPoolDocument,
  BulkAddCompaniesToPoolDocument,
  BulkRemoveCompaniesFromPoolDocument,
  CompanyStatus,
  MatchCompanyListDocument,
  RemoveCompanyFromPoolDocument,
} from "@/graphql/generated/operations";
import { MATCH_COMPANY_LIMIT } from "./match-constants";
import { throwIfValidationRejected, useValidationFallback } from "./use-match-editor";

// THE COMPANY POOL OF ONE MATCH — read once, written by both company screens.
//
// It is one hook and not two because the pool is one thing seen from two places: the pool step
// fills it, and the ranking step ranks what is in it. Both mount the same query with the
// same variables, so Apollo answers the second from cache and a write on either surface refetches
// for both. Duplicating the wiring per screen would have let the tick and the pool disagree about
// what is in the pool.
//
// Deliberately NOT folded into use-match-editor. That hook is the person match's editor — design
// autosave, candidates, solver, results — and every company match would pay for a pool query it
// has no use for (and vice versa: three quarters of the modes would pay for this one). The pool is
// mode-specific data, so it is loaded by the surfaces that are mode-specific.

/** One company in the pool, flattened for the two lists that draw it. */
export interface CompanyPoolRow {
  /** The `match_company` row id. This is the delete handle — NOT the company id. */
  readonly id: string;
  readonly companyId: string;
  readonly name: string;
  /** For a Branche criterion's actual value; null when the company carries no industry. */
  readonly industryId: string | null;
  /** For a size criterion's actual value. null means "no value", never "zero employees". */
  readonly employeeCount: number | null;
  /**
   * A dormant company (the company account model). genesis keeps it in the pool and in the ranking
   * on purpose — the pool is an explicit choice by the user, and dropping it silently would be a
   * rule nobody decided — so both screens state it rather than acting on it.
   */
  readonly inactive: boolean;
}

export interface UseMatchCompanyPoolResult {
  readonly rows: CompanyPoolRow[];
  /**
   * How many companies the pool holds, ACCORDING TO THE SERVER (`totalCount` over the whole pool).
   * Not `rows.length`: the read is capped, so at the cap the array length would stop being the
   * pool's size and the header would understate it.
   *
   * A 0 HERE IS NOT PROOF OF AN EMPTY POOL. There is no `totalCount` while the read is in flight
   * and none after it failed, and the fallback is `rows.length` — which is 0 in both cases, the
   * same number a genuinely empty pool reports. A caller that reads 0 as a fact without consulting
   * {@link loading} and {@link readError} first will tell the user to go and fill a pool that is
   * full.
   */
  readonly total: number;
  readonly loading: boolean;
  /** The pool is at genesis's cap — every add is refused until something is removed. */
  readonly full: boolean;
  readonly byCompanyId: ReadonlyMap<string, CompanyPoolRow>;
  /**
   * The last refusal, already localized and safe to render (@adr SPARK-0010-no-raw-server-errors-in-ui@1). Null when the last write
   * succeeded. Writes never throw at the call site: a pool screen has somewhere legible to put the
   * sentence, which is better than a rejected promise nobody catches.
   */
  readonly refusal: string | null;
  readonly clearRefusal: () => void;
  /**
   * The READ's failure, already localized and safe to render (@adr SPARK-0010-no-raw-server-errors-in-ui@1). Null while the read is
   * fine.
   *
   * Deliberately NOT folded into {@link refusal}. A refusal is about something the viewer just
   * did and can undo; a read failure means the pool on this page is unknown — including
   * {@link total}, which then reports 0. They have different repairs, so a surface that renders
   * them as one sentence makes the reader guess which of the two happened.
   */
  readonly readError: string | null;
  /**
   * Re-runs the read. The way out of {@link readError}: a failed read leaves every count on the
   * screen wrong, and a wrong count with no retry is a dead end (@adr SPARK-0010-no-raw-server-errors-in-ui@1 rejects silent failure).
   */
  readonly reload: () => Promise<void>;
  // Every write answers whether it happened. Not for error handling — {@link refusal} already
  // carries the sentence — but so a caller can avoid claiming an outcome it did not get: the bulk
  // add reports "N were added" only when N actually were.
  readonly add: (companyId: string) => Promise<boolean>;
  readonly addMany: (companyIds: readonly string[]) => Promise<boolean>;
  readonly remove: (rowId: string) => Promise<boolean>;
  readonly removeAll: () => Promise<boolean>;
}

export function useMatchCompanyPool(opts: {
  matchId: string;
  /**
   * Whether this viewer may write the pool at all — genesis's rule is owner OR discover WRITE, and
   * the caller reads it off the match (see `canEditPool` on the editor). Every write here checks it
   * first, so a disabled control and a bypassed one refuse the same way.
   */
  canEdit: boolean;
  /**
   * Called after a write that changed WHICH companies are in the pool. The ranking is computed from
   * the pool, so an existing result is stale the moment the pool moves — this is where the caller
   * drops it, exactly as the person path clears the solver result on a candidate change.
   */
  onPoolChanged?: () => void;
}): UseMatchCompanyPoolResult {
  const { matchId, canEdit, onPoolChanged } = opts;
  const intl = useIntl();
  const validationFallback = useValidationFallback();
  const [refusal, setRefusal] = useState<string | null>(null);

  const poolQuery = useQuery(MatchCompanyListDocument, {
    variables: { input: { first: MATCH_COMPANY_LIMIT, where: { matchId: { eq: matchId } } } },
    fetchPolicy: "cache-and-network",
  });

  const [addOne] = useMutation(AddCompanyToPoolDocument);
  const [addBulk] = useMutation(BulkAddCompaniesToPoolDocument);
  const [removeOne] = useMutation(RemoveCompanyFromPoolDocument);
  const [removeBulk] = useMutation(BulkRemoveCompaniesFromPoolDocument);

  const rows = useMemo<CompanyPoolRow[]>(() => {
    const nodes = poolQuery.data?.matchCompanies.nodes ?? [];
    return nodes.flatMap((node) => {
      // A row whose company did not come back is a row that cannot be drawn or judged. The foreign
      // key means it cannot happen; skipping is the honest response if it ever does, rather than a
      // nameless row with an Entfernen button and no way to tell which company it removes.
      if (!node.company) return [];
      return [
        {
          id: node.id,
          companyId: node.company.id,
          name: node.company.name,
          industryId: node.company.industry?.id ?? null,
          employeeCount: node.company.employeeCount ?? null,
          inactive: node.company.status === CompanyStatus.Inactive,
        },
      ];
    });
  }, [poolQuery.data]);

  const byCompanyId = useMemo(() => {
    const map = new Map<string, CompanyPoolRow>();
    for (const row of rows) map.set(row.companyId, row);
    return map;
  }, [rows]);

  const total = poolQuery.data?.matchCompanies.totalCount ?? rows.length;

  // A FAILED READ IS A SENTENCE, not a silent 0 in `total`. Computed once per error rather than
  // per render, because userErrorMessage logs a diagnostic as a side effect (@adr SPARK-0010-no-raw-server-errors-in-ui@1) and a
  // message rebuilt on every render would log on every render. `intl` is out of the deps for the
  // same reason: the locale is not what changed.
  const readError = useMemo(
    () => (poolQuery.error ? userErrorMessage(intl, poolQuery.error) : null),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [poolQuery.error],
  );

  const capMessage = useCallback(
    () =>
      intl.formatMessage(
        {
          id: "match.companyPool.error.full",
          defaultMessage:
            "Ein Match führt höchstens {max, number} Unternehmen. Entferne eines, um ein weiteres aufzunehmen.",
        },
        { max: MATCH_COMPANY_LIMIT },
      ),
    [intl],
  );

  // ONE PLACE TURNS A FAILED POOL WRITE INTO A SENTENCE, and it never renders genesis's own prose
  // (@adr SPARK-0010-no-raw-server-errors-in-ui@1). The cap is the one refusal spark can name exactly: genesis catalogues it, so it is
  // matched on `extensions.code` and answered with the repair ("remove one"), while anything else
  // falls to the generic message plus a support code.
  const explain = useCallback(
    (error: unknown): string =>
      graphQlErrorExtensions(error, TOO_MANY_MATCH_COMPANIES) ? capMessage() : userErrorMessage(intl, error),
    [capMessage, intl],
  );

  const write = useCallback(
    async (run: () => Promise<void>): Promise<boolean> => {
      setRefusal(null);
      try {
        await run();
        return true;
      } catch (error) {
        setRefusal(explain(error));
        return false;
      }
    },
    [explain],
  );

  const refetch = useCallback(async () => {
    await poolQuery.refetch();
  }, [poolQuery]);

  const add = useCallback(
    async (companyId: string) => {
      if (!canEdit) return false;
      // Refused here as well as by the disabled button: the button is drawn from the same count, so
      // this is the case where another editor filled the last slot since this page read the pool.
      if (total >= MATCH_COMPANY_LIMIT) {
        setRefusal(capMessage());
        return false;
      }
      return write(async () => {
        const res = await addOne({
          variables: { input: { data: { match: { id: matchId }, company: { id: companyId } } } },
        });
        throwIfValidationRejected(
          res.data?.createMatchCompany,
          "ValidateMatchCompanyResult",
          validationFallback,
        );
        onPoolChanged?.();
        await refetch();
      });
    },
    [addOne, canEdit, capMessage, matchId, onPoolChanged, refetch, total, validationFallback, write],
  );

  const addMany = useCallback(
    async (companyIds: readonly string[]) => {
      if (!canEdit || companyIds.length === 0) return false;
      return write(async () => {
        await addBulk({
          variables: {
            input: { match: { id: matchId }, data: companyIds.map((id) => ({ company: { id } })) },
          },
        });
        onPoolChanged?.();
        await refetch();
      });
    },
    [addBulk, canEdit, matchId, onPoolChanged, refetch, write],
  );

  const remove = useCallback(
    async (rowId: string) => {
      if (!canEdit) return false;
      return write(async () => {
        await removeOne({ variables: { input: { id: rowId } } });
        onPoolChanged?.();
        await refetch();
      });
    },
    [canEdit, onPoolChanged, refetch, removeOne, write],
  );

  const removeAll = useCallback(async () => {
    if (!canEdit || rows.length === 0) return false;
    return write(async () => {
      // The bulk delete takes POOL ROW ids and needs the match: genesis narrows the ids to that
      // match before deleting, so a row belonging to another match cannot be removed by proving
      // rights on this one.
      await removeBulk({
        variables: { input: { match: matchId, companies: rows.map((r) => r.id) } },
      });
      onPoolChanged?.();
      await refetch();
    });
  }, [canEdit, matchId, onPoolChanged, refetch, removeBulk, rows, write]);

  return {
    rows,
    total,
    loading: poolQuery.loading && !poolQuery.data,
    full: total >= MATCH_COMPANY_LIMIT,
    byCompanyId,
    refusal,
    clearRefusal: useCallback(() => setRefusal(null), []),
    readError,
    reload: refetch,
    add,
    addMany,
    remove,
    removeAll,
  };
}
